import React, { memo, useState } from "react";

import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Box, Button, Stack } from "@origin-digital/ods-core";
import { screenNames } from "@/constants";

import { useRedirect } from "@/hooks/useRedirect";
import { useRestartForm } from "@/hooks/useRestartForm";
import { useMediaQuery, useTheme } from "@mui/material";
import { IconChevronLeft, IconChevronRight } from "@origin-digital/ods-icons";

/**
 * A navigation component provides Back and Next buttons.
 *
 * @param onBackRoute - a func returns the previous route
 * @param onNextRoute - a func returns the next route
 * @param onNext - a func does validation before go to next screen
 * @param hideBack - hide Back button
 */

// @ts-ignore
const RouterBoxContainer = styled.div(() => ({
  "&": {
    zIndex: "999",
    display: "flex",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    marginTop: "48px",

    "&.fixed": {
      position: "fixed",
      padding: "20px 30px",
      backgroundColor: "white",
      width: "calc(100% - 60px)",
      bottom: "0px",
      left: "0px",
    },

    "&.three-phase": {
      flexDirection: "column",
      position: "fixed",
      padding: "20px 30px",
      backgroundColor: "white",
      width: "calc(100% - 60px)",
      bottom: "0px",
      left: "0px",
    },
  },
}));

export const RouterActions = memo(
  ({
    onBackRoute,
    onNextRoute,
    onNext,
    hideBack = false,
    nextLabel = "Next",
    nextIcon = <IconChevronRight color="white" />,
    nextPosition = "right",
    isIconEnd = false,
  }: {
    onBackRoute?: () => Promise<string>;
    onNextRoute?: () => Promise<string>;
    onNext?: () => Promise<boolean>;
    hideBack?: boolean;
    nextLabel?: string;
    nextIcon?: JSX.Element | null;
    nextPosition?: "right" | "left";
    isIconEnd?: boolean;
  }) => {
    const navigate = useNavigate();
    const { goBackRoute, goNextRoute } = useRedirect();
    const isSolarBatteryOutlook = location.pathname.includes(
      screenNames.solarBatteryOutlook
    );

    const isThreePhase = location.pathname.includes(screenNames.threePhase);

    const [loading, setLoading] = useState(false);

    const { isFormExpired } = useRestartForm();

    const theme = useTheme();
    const isMdDown = useMediaQuery(theme.breakpoints.down("md")); // Check the viewport width is below md = 768px
    const isXsDown = useMediaQuery(theme.breakpoints.down("xs")); // Check the viewport width is below xs = 375px

    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const checkIfNeedToRestartForm = () => {
      if (isFormExpired()) {
        navigate("/");
        return true;
      }

      return false;
    };

    const doBack = async () => {
      if (checkIfNeedToRestartForm()) {
        return;
      }

      if (onBackRoute) {
        const to = await onBackRoute();
        goBackRoute(to);
      } else {
        navigate(-1);
      }
    };

    const doNext = async () => {
      if (checkIfNeedToRestartForm()) {
        return;
      }

      setLoading(true);

      try {
        // only navigate if one of the below is valid
        // - next route is provided AND
        // - in case validation is provided, it ensure the input is valid
        // - or validation is not required (= not provided)
        if (onNextRoute && (!onNext || (onNext && (await onNext())))) {
          const to = await onNextRoute();

          // this caters for a case you open more than one tab, that's distinct from each other
          // then one tab (or all tabs) is closed by some reason such as close by intended,
          // browser is crashed, or network error
          // reopening tab will show persisted data from previous inputs
          goNextRoute(to);
        }
      } finally {
        setLoading(false);
      }
    };

    const isFixed = () => {
      const viewportHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      return documentHeight > viewportHeight && isMdDown;
    };

    const renderButton = () => {
      return (
        <>
          {onNextRoute && (
            <Button
              muiProps={{ endIcon: nextIcon }}
              onClick={doNext}
              spinner={loading}
            >
              {nextLabel}
            </Button>
          )}
          {hideBack && nextPosition === "right" && <div />}
          {!hideBack && (
            <Button
              variant="text"
              muiProps={{ startIcon: <IconChevronLeft color="critical" /> }}
              onClick={doBack}
            >
              Back
            </Button>
          )}
        </>
      );
    };

    if (isSolarBatteryOutlook && isMdDown) {
      const scrollUp = "Look at other system sizes";
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          marginTop="small"
        >
          <Stack space={"large"} alignX="center">
            <Stack space={"medium"} alignX="center">
              <Button variant="outlined" onClick={scrollToTop} fullWidth={true}>
                {scrollUp}
              </Button>
              <Button
                muiProps={{}}
                onClick={doNext}
                spinner={loading}
                fullWidth={true}
              >
                {nextLabel}
              </Button>
            </Stack>
            <Button
              variant="text"
              muiProps={{ startIcon: <IconChevronLeft color="critical" /> }}
              onClick={doBack}
            >
              Back
            </Button>
          </Stack>
        </Box>
      );
    } else if (isIconEnd) {
      return (
        <Button
          muiProps={{ endIcon: <IconChevronRight color="critical" /> }}
          variant="text"
          size="small"
          noTextPadding
          onClick={doNext}
        >
          {nextLabel}
        </Button>
      );
    } else if (isThreePhase && isXsDown) {
      return (
        <RouterBoxContainer className="three-phase">
          {renderButton()}
        </RouterBoxContainer>
      );
    } else {
      return (
        <RouterBoxContainer className={isFixed() ? "fixed" : ""}>
          {renderButton()}
        </RouterBoxContainer>
      );
    }
  }
);
